import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faInfoCircle,
  faShieldAlt,
  faChartLine,
  faLayerGroup,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';

const WFAResultsHero = () => (
  <div className="rounded-lg overflow-hidden shadow-2xl shadow-black/30 border border-white/[0.06] font-mono" style={{ background: '#0a0e14' }}>
    {/* Workspace tab bar */}
    <div className="flex items-center border-b border-white/[0.06] px-1" style={{ background: '#181825' }}>
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b-2 border-[#9d4edd] text-[10px] text-[#9d4edd]">
        <FontAwesomeIcon icon={faChartLine} className="text-[9px]" />
        WFA Results
      </div>
      <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-gray-500">
        Console
      </div>
    </div>

    {/* 2x2 grid of WFA result cards */}
    <div className="p-3 grid grid-cols-2 gap-2.5">
      {/* WFA Validation Status */}
      <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-3 flex flex-col">
        <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/[0.06]">
          <FontAwesomeIcon icon={faShieldAlt} className="text-[10px] text-blue-400" />
          <span className="text-[9px] font-semibold text-gray-300 uppercase tracking-wide">WFA Validation</span>
        </div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <FontAwesomeIcon icon={faCheckCircle} className="text-lime-200 text-xs" />
          <span className="text-sm font-bold text-lime-200 uppercase tracking-wide">Pass</span>
        </div>
        <div className="text-[9px] text-gray-400 mb-2 leading-snug">Forward performance retained with acceptable stability.</div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mb-0.5">Validation Quality</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">WFA Efficiency</span><span className="font-bold text-white">0.85</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">Degradation</span><span className="font-bold text-white">21%</span></div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mt-1.5 mb-0.5">Outcome Context</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">OOS Sharpe</span><span className="text-gray-300">1.42</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">Success Rate</span><span className="text-gray-300">75%</span></div>
      </div>

      {/* Parameter Stability */}
      <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-3 flex flex-col">
        <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/[0.06]">
          <FontAwesomeIcon icon={faLayerGroup} className="text-[10px] text-purple-400" />
          <span className="text-[9px] font-semibold text-gray-300 uppercase tracking-wide">Parameter Stability</span>
        </div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mb-0.5">Drift Analysis</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">Avg Drift</span><span className="font-bold text-white">0.12</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">Max Drift</span><span className="font-bold text-white">0.28</span></div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mt-1.5 mb-0.5">Anchoring</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">Region Stability</span><span className="text-gray-300">High</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">Windows Anchored</span><span className="text-gray-300">6/8</span></div>
        <div className="mt-auto pt-1.5 border-t border-white/[0.04]">
          <div className="text-[9px] text-gray-500 italic">Low drift indicates structural edge.</div>
        </div>
      </div>

      {/* Performance Consistency */}
      <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-3 flex flex-col">
        <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/[0.06]">
          <FontAwesomeIcon icon={faChartLine} className="text-[10px] text-green-400" />
          <span className="text-[9px] font-semibold text-gray-300 uppercase tracking-wide">Performance</span>
        </div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mb-0.5">Return Distribution</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">CV</span><span className="font-bold text-white">0.31</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-400">Trend</span><span className="font-bold text-white">Stable</span></div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mt-1.5 mb-0.5">Win Rate Stability</div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">Range</span><span className="text-gray-300">52-61%</span></div>
        <div className="flex justify-between text-[10px]"><span className="text-gray-500">Variance</span><span className="text-gray-300">Low</span></div>
        <div className="mt-auto pt-1.5 border-t border-white/[0.04]">
          <div className="text-[9px] text-gray-500 italic">Consistent across windows.</div>
        </div>
      </div>

      {/* Live Readiness */}
      <div className="bg-[#181825] rounded-lg border border-white/[0.06] p-3 flex flex-col">
        <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/[0.06]">
          <FontAwesomeIcon icon={faRocket} className="text-[10px] text-cyan-400" />
          <span className="text-[9px] font-semibold text-gray-300 uppercase tracking-wide">Live Readiness</span>
        </div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wide bg-lime-300/10 text-lime-200 font-semibold">Eligible</span>
        </div>
        <div className="text-[8px] uppercase tracking-wider text-gray-500 mb-0.5">Deployment Criteria</div>
        <div className="space-y-0.5 text-[10px]">
          <div className="flex justify-between"><span className="text-gray-400">WFA Validated</span><FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px]" /></div>
          <div className="flex justify-between"><span className="text-gray-400">Stability OK</span><FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px]" /></div>
          <div className="flex justify-between"><span className="text-gray-400">Risk Bounded</span><FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px]" /></div>
          <div className="flex justify-between"><span className="text-gray-400">Sufficient Depth</span><FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px]" /></div>
        </div>
      </div>
    </div>

    {/* Bottom dock */}
    <div className="border-t border-white/[0.06]" style={{ background: '#181825' }}>
      <div className="flex items-center px-2 py-1 border-b border-white/[0.06]" style={{ background: '#0f1923' }}>
        <span className="text-[9px] text-[#9d4edd] border-b border-[#9d4edd] px-2 py-0.5">Output</span>
        <span className="text-[9px] text-gray-400 px-2 py-0.5">Console</span>
      </div>
      <div className="px-2 py-1.5 font-mono text-[10px] space-y-0.5">
        <div className="flex items-start gap-1.5">
          <span className="text-gray-500 select-none">[14:23:08]</span>
          <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-[8px] mt-0.5" />
          <span className="text-green-300">WFA analysis complete -- 8 windows validated</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span className="text-gray-500 select-none">[14:23:08]</span>
          <FontAwesomeIcon icon={faInfoCircle} className="text-cyan-400 text-[8px] mt-0.5" />
          <span className="text-cyan-300">Verdict: PASS -- ready for Adaptive Flow</span>
        </div>
      </div>
    </div>
  </div>
);

export default WFAResultsHero;
